import { Vector2 } from "../core/types";
import { scDeclareSceneUniformBuffer } from "./shaderBasePass";
import { scGetCommonFuncsDeclarations, scPBRDeclarations } from "./shaderCommon";
import { scGetDestrTileCommonFuncsDecl } from "./shaderDestrTiles";


export function scGetSDFFuncsDeclarations(sceneSDFFunc : string)
{
	return (/* glsl */`

	#define SDF_MAX_STEPS 64
	#define SDF_EPSILON 0.05
	

	float sdSphere( vec3 pointPos, vec3 spherePos, float r )
	{
	  	return length(pointPos - spherePos) - r;
	}
	float sdPlane(vec3 p, vec3 n, float d )
	{
	  	return dot(p,n) + d;
	}

	float sdBox( vec3 curPos, vec3 p, vec3 b )
	{
	    vec3 d = abs(curPos - p) - b;
	    return min(max(d.x,max(d.y,d.z)),0.0) + length(max(d,0.0));
	}

	float sdRoundBox( vec3 curPos, vec3 p, vec3 b, float r )
	{
	  	vec3 q = abs(curPos - p) - b + r;
	  	return length(max(q,0.0)) + min(max(q.x,max(q.y,q.z)),0.0) - r;
	}

	float sdCapsule( vec3 p, vec3 a, vec3 b, float r )
	{
	  vec3 pa = p - a, ba = b - a;
	  float h = clamp( dot(pa,ba)/dot(ba,ba), 0.0, 1.0 );
	  return length( pa - ba*h ) - r;
	}

	float sdCappedCone( vec3 p, vec3 a, vec3 b, float ra, float rb )
	{
	  float rba  = rb-ra;
	  float baba = dot(b-a,b-a);
	  float papa = dot(p-a,p-a);
	  float paba = dot(p-a,b-a)/baba;
	  float x = sqrt( papa - paba*paba*baba );
	  float cax = max(0.0,x-((paba<0.5)?ra:rb));
	  float cay = abs(paba-0.5)-0.5;
	  float k = rba*rba + baba;
	  float f = clamp( (rba*(x-ra)+paba*baba)/k, 0.0, 1.0 );
	  float cbx = x-ra - f*rba;
	  float cby = paba - f;
	  float s = (cbx<0.0 && cay<0.0) ? -1.0 : 1.0;
	  return s*sqrt( min(cax*cax + cay*cay*baba,
	                     cbx*cbx + cby*cby*baba) );
	}

	float hash(vec3 p)  
	{
	    p  = 17.0*fract( p*0.3183099+vec3(.11,.17,.13) );
	    return fract( p.x*p.y*p.z*(p.x+p.y+p.z) );
	}

	float sph( ivec3 i, vec3 f, ivec3 c )
	{
	   // random radius at grid vertex i+c
	   float rad = 0.5*hash(vec3(i+c));
	   // distance to sphere at grid vertex i+c
	   return length(f-vec3(c)) - rad; 
	}

	float sdBase( vec3 p )
	{
	   ivec3 i = ivec3(floor(p));
	    vec3 f =       fract(p);
	   // distance to the 8 corners spheres
	   return min(min(min(sph(i,f,ivec3(0,0,0)),
	                      sph(i,f,ivec3(0,0,1))),
	                  min(sph(i,f,ivec3(0,1,0)),
	                      sph(i,f,ivec3(0,1,1)))),
	              min(min(sph(i,f,ivec3(1,0,0)),
	                      sph(i,f,ivec3(1,0,1))),
	                  min(sph(i,f,ivec3(1,1,0)),
	                      sph(i,f,ivec3(1,1,1)))));
	}

	float sdVerticalCapsule( vec3 curPos, vec3 p, float h, float r )
	{
		//p.y -= h * 0.5;
		p = curPos - p;
	  	p.y -= clamp( p.y, 0.0, h );
	  	return length( p ) - r;
	}

	float sdTiledPyramid(vec2 p, float flow){
    
		// Cell ID and local coordinates.
		vec2 ip = floor(p);
		p -= ip + .5;
	
		// Transcendental angle function... Made up on the spot.
		float ang = dot(sin(ip/4. - cos(ip.yx/2. + flow))*6.2831, vec2(.5));
		// Offset point within the cell.
		vec2 offs = vec2(cos(ang), sin(ang))*.35;
		 
		// Linear pyramid shading, according to the offset point.
		if(p.x<offs.x)  p.x = 1. - (p.x + .5)/abs(offs.x  + .5);
		else p.x = (p.x - offs.x)/(.5 - offs.x);
	
		if(p.y<offs.y) p.y = 1. - (p.y + .5)/abs(offs.y + .5);
		else p.y = (p.y - offs.y)/(.5 - offs.y);
	
		// Return the offset pyramid distance field.
		return 1. - max(p.x, p.y);
	}


	float tileHeightMap(vec2 uv, float tileHeight) {
		// Adjust uv coordinates based on tile size
		vec2 tileUV = 2.0 * (fract(uv) - 0.5); // Normalize tile coords to [-0.5, 0.5]
		
		float h = 0.0;
		
		float thres = 0.975;
		
		h += smoothstep(thres, 1.0, abs(tileUV.x)) * tileHeight;
		h = max(h, smoothstep(thres, 1.0, abs(tileUV.y)) * tileHeight);
		
		// Blend tile height with border edge for smooth transition
		return 1.0 - min(1.0, h);
	}

	float sdCustomMesh(vec3 curPos, highp sampler3D texture, vec3 meshBoundsCenter, vec3 meshBoundsExtent, float meshScale, bool bRayMeshIntersection)
	{
		float curD = 20.0;
		vec3 sdfMeshBoxMin = meshBoundsCenter - meshBoundsExtent;
		vec3 sdfMeshBoxMax = meshBoundsCenter + meshBoundsExtent;
		//sample SDF texture
		if(bRayMeshIntersection)
		{
			vec3 uvw = MapToRange(curPos, sdfMeshBoxMin, sdfMeshBoxMax, vec3(0.0), vec3(1.0));
			float distToSDFMeshAABB = sdBox(curPos, meshBoundsCenter, meshBoundsExtent);
			distToSDFMeshAABB = max(0.0, distToSDFMeshAABB);
			uvw = clamp(uvw, vec3(0.0), vec3(1.0));
			//if(all(greaterThan(uvw, vec3(0.0))) && all(lessThan(uvw, vec3(1.0))))
			{
				float meshDist = textureLod(texture, uvw, 0.0).r;
				//meshDist = 0.0;
				meshDist *= meshScale;
				curD = min(curD, meshDist + distToSDFMeshAABB);
			}
		}

		return curD;

	}

	`+sceneSDFFunc+ /* glsl */`
	
	struct SDFMarchResult
	{
		bool bHit;
		vec3 HitPosWS;
		float tRes;
		int NumSteps; //Debug
	};

	SDFMarchResult MarchSDFScene(vec3 rayOrigin, vec3 rayDir)
	{
		bool bHit = false;
		vec3 hitPositionWS = rayOrigin;
		
		// March along the ray until we hit something or reach maximum distance
		float t = 0.01;
	
		bool bRayMeshIntersection = false;
	#ifndef SDF_DISABLE_MESH
		{
			vec4 rayMeshIntersectionRes = RayAABBIntersection(rayOrigin, rayDir, SceneBuffer.SDFMeshBoundsCenter, SceneBuffer.SDFMeshBoundsExtent);
			bRayMeshIntersection = rayMeshIntersectionRes.x > 0.0;
			if(bRayMeshIntersection)
			{
				t = rayMeshIntersectionRes.z;
			}
		}
	#endif

	#ifdef SECOND_SDF_MESH
		bool bRayMeshIntersection2 = false;
		{
			vec4 rayMeshIntersectionRes = RayAABBIntersection(rayOrigin, rayDir, SceneBuffer.SDFMeshBoundsCenter2, SceneBuffer.SDFMeshBoundsExtent2);
			bRayMeshIntersection2 = rayMeshIntersectionRes.x > 0.0;
			if(bRayMeshIntersection2)
			{
				t = min(t, rayMeshIntersectionRes.z);
			}
		}
	#endif //SECOND_SDF_MESH
			
		int numSteps = 0;
		for (int i = 0; i < SDF_MAX_STEPS; i++) {
		#if 0
			float hitDist = 0.0001 * t;
		#else
			float hitDist = 0.001 * t;
			hitDist += 0.001 * float(i);
		#endif
			hitPositionWS = rayOrigin + t * rayDir;

		#ifdef SECOND_SDF_MESH
			float dist = sceneSDF(hitPositionWS, bRayMeshIntersection, bRayMeshIntersection2);
		#else
			float dist = sceneSDF(hitPositionWS, bRayMeshIntersection, false);
		#endif

		#if 1 //negative dist is a hit
			if(dist < 0.0)
			{
				bHit = true;
				t += dist;
				hitPositionWS = rayOrigin + t * rayDir;
				break; 
			}
		#endif
			if (abs(dist) < abs(hitDist))
			{
				bHit = true;
				break; 
			}
			else if( t > (SDF_MAX_DISTANCE))
			{
				break;
			}
			t += dist * (0.95 + min(0.05, float(i) * 0.0001)); // Move along the ray by the distance to the nearest surface
			numSteps+=1;
		}

		SDFMarchResult res;
		res.bHit = bHit;
		res.HitPosWS = hitPositionWS;
		res.tRes = t;
		res.NumSteps = numSteps;

		return res;

	}

	vec3 calcNormal( in vec3 pos, bool bRayMeshIntersection, bool bRayMeshIntersection2 )
	{
		// inspired by tdhooper and klems - a way to prevent the compiler from inlining map() 4 times
	    vec3 n = vec3(0.0);
	    for( int i=0; i<4; i++ )
	    {
	        vec3 e = 0.5773*(2.0*vec3((((i+3)>>1)&1),((i>>1)&1),(i&1))-1.0);
	        n += e*sceneSDF(pos+0.0005*e, bRayMeshIntersection, bRayMeshIntersection2);
	      //if( n.x+n.y+n.z>100.0 ) break;
	    }
	    return normalize(n);
	}

	// https://iquilezles.org/articles/nvscene2008/rwwtt.pdf
	float calcAO( in vec3 pos, in vec3 nor )
	{
		float occ = 0.0;
	    float sca = 1.0;
	    for( int i=0; i<7; i++ )
	    {
			//const float stepSize = 0.25;
			const float stepSize = 0.2;
	        float h = 0.01 + 0.12*float(i) * stepSize;
	        float d = sceneSDF( pos + h*nor, false, false );
	        occ += (h-d)*sca;
	        sca *= 0.85;
	        if( occ>0.35 ) break;
	    }
	    return clamp( 1.0 - 3.0*occ, 0.0, 1.0 );
	}

	float CalcAOPrecise( in vec3 pos, in vec3 nor )
	{
		float occ = 0.0;
	    float sca = 1.0;

		bool bRayMeshIntersection = RayAABBIntersection(pos, nor, SceneBuffer.SDFMeshBoundsCenter, SceneBuffer.SDFMeshBoundsExtent).x > 0.0;
	#ifdef SECOND_SDF_MESH
		bool bRayMeshIntersection2 = RayAABBIntersection(pos, nor, SceneBuffer.SDFMeshBoundsCenter2, SceneBuffer.SDFMeshBoundsExtent2).x > 0.0;
	#endif

	    for( int i=0; i<3; i++ )
	    {
			//const float stepSize = 0.25;
			float ran = hash( uint(gl_FragCoord.x) * uint(i) + 1920U* uint(gl_FragCoord.y) + (1920U*1080U) );  
			//ran = 1.0;
			float stepSize = 0.1 + ran * 0.01;
	        float h = stepSize + stepSize * float(i);
		#ifdef SECOND_SDF_MESH
			float d = sceneSDF( pos + h*nor, bRayMeshIntersection, bRayMeshIntersection2 );
	    #else
			float d = sceneSDF( pos + h*nor, bRayMeshIntersection, false );
		#endif//SECOND_SDF_MESH
			float diff = h-d;
	        occ += pow(diff, 1.0)*0.1*sca;
	        //sca *= 0.1;
	        if( occ>0.35 ) break;
	    }
	    return clamp( 1.0 - 3.0*occ, 0.0, 1.0 );
	}

	float CalcAOSpreadHemisphere( in vec3 p, in vec3 n, in float maxDist, in float falloff )
	{
		const int nbIte = 16;
		const float nbIteInv = 1./float(nbIte);
		const float rad = 1.-1. * nbIteInv; //Hemispherical factor (self occlusion correction)

		float ao = 0.0;

		for( int i=0; i<nbIte; i++ )
		{
			float l = hashf(float(i)) * maxDist;
			vec3 rd = normalize(n + randomHemisphereDir(n, l ) * rad /* * 0.0 */) * l; // mix direction with the normal for self occlusion problems!

			ao += (l - max(sceneSDF( p + rd, false, false ), 0.)) / maxDist * falloff;
		}

		return clamp( 1.-ao * nbIteInv, 0., 1.);
	}
	
	float CalcAOSpread( in vec3 pos, in vec3 nor, float ra )
	{
	    float occ = 0.0;
	    {
			int i = 1;
	        float h = 0.01 + 4.0*pow(float(i)/31.0,2.0);
	        vec2 an = hash2( ra + float(i)*13.1 )*vec2( 3.14159, 6.2831 );
	        vec3 dir = vec3( sin(an.x)*sin(an.y), sin(an.x)*cos(an.y), cos(an.x) );
			
			vec3 randomVec = vec3(0.0);
			float d = 1.0;

			float occScale = 0.25;
			
			float randDirScale = 0.1;
			vec3 tangentBasis = normalize(vec3(1.0, 0.0, 1.0) + dir * randDirScale);
			vec3 tangent   = normalize(tangentBasis - nor * dot(tangentBasis, nor));
			vec3 bitangent = cross(nor, tangent);
			mat3 TBN       = mat3(tangent, bitangent, nor);
			float stepSize = 0.5 + 0.75 * float(i) + ra * 0.02;
			
			randomVec = normalize(vec3(1.0, 0.0, 0.0) + vec3(dir.xy * 0.1, 0.0));
			randomVec = normalize(TBN * randomVec);
			d = sceneSDF(pos + randomVec * stepSize, false, false);
			if(d < 0.0)
			{
				occ += abs(d) * occScale;
			}

			randomVec = normalize(vec3(-1.0, 0.0, 0.0) + vec3(dir.xy * 0.1, 0.0));
			randomVec = normalize(TBN * randomVec);
			d = sceneSDF(pos + randomVec * stepSize, false, false);
			if(d < 0.0)
			{
				occ += abs(d) * occScale;
			}

			randomVec = normalize(vec3(0.0, 1.0, 0.0) + vec3(dir.xy * 0.1, 0.0));
			randomVec = normalize(TBN * randomVec);
			d = sceneSDF(pos + randomVec * stepSize, false, false);
			if(d < 0.0)
			{
				occ += abs(d) * occScale;
			}

			randomVec = normalize(vec3(0.0, -1.0, 0.0) + vec3(dir.xy * 0.1, 0.0));
			randomVec = normalize(TBN * randomVec);
			d = sceneSDF(pos + randomVec * stepSize, false, false);
			if(d < 0.0)
			{
				occ += abs(d) * occScale;
			}

			#if 1//ADDITIONAL CORNERS
			occScale *= 0.1;
			float rndVecScale = 0.1;
			randomVec = normalize(vec3(1.0, 1.0, 0.0) + vec3(dir.xy * rndVecScale, 0.0));
			randomVec = normalize(TBN * randomVec);
			stepSize *= 2.5;
			d = sceneSDF(pos + randomVec * stepSize, false, false);
			if(d < 0.0)
			{
				occ += (abs(d)) * occScale;
			}
			randomVec = normalize(vec3(-1.0, -1.0, 0.0) + vec3(dir.xy * rndVecScale, 0.0));
			randomVec = normalize(TBN * randomVec);
			d = sceneSDF(pos + randomVec * stepSize, false, false);
			if(d < 0.0)
			{
				occ += (abs(d)) * occScale;
			}
			randomVec = normalize(vec3(-1.0, 1.0, 0.0) + vec3(dir.xy * rndVecScale, 0.0));
			randomVec = normalize(TBN * randomVec);
			d = sceneSDF(pos + randomVec * stepSize, false, false);
			if(d < 0.0)
			{
				occ += (abs(d)) * occScale;
			}
			randomVec = normalize(vec3(1.0, -1.0, 0.0) + vec3(dir.xy * rndVecScale, 0.0));
			randomVec = normalize(TBN * randomVec);
			d = sceneSDF(pos + randomVec * stepSize, false, false);
			if(d < 0.0)
			{
				occ += (abs(d)) * occScale;
			}
			#endif


			occ *= occ * occ;
			//occ *= occ;


			
	        //occ += 10.0 * clamp( 5.0*sceneSDF( pos + h*dir ) * 0.1 /* / h */, -1.0, 1.0);
	    }

		occ = 1.0 - clamp( occ, 0.0, 1.0 );
	    return occ;

	}

	

	
	`);
}



export function scGetSDFShadowDeclarations()
{
	return (/* glsl */`

	/* float sceneSDFShadow(vec3 curPos, bool bRayMeshIntersection)
	{
		float curD = SDF_MAX_DISTANCE;
		
		//main painting pos
		{
			curD = min(curD, sdBox(curPos, SceneBuffer.OccluderPlaneCenter, SceneBuffer.OccluderPlaneExtent));
		}
		return curD;
	} */

	
	

	
	
	`);
}


export function GetShaderSourceSDFRenderPS(sceneSDFFunc : string) {
    return /* glsl */ `#version 300 es
	
	precision highp float;
	precision highp sampler2D;

	layout(location = 0) out vec3 outColor;

	in vec2 vsOutTexCoords;

	uniform sampler2D ProjectorTexture;
	uniform sampler2D BlueNoiseTexture;
	uniform highp sampler3D SDFTexture;
	uniform highp sampler3D SDFTexture2;

	uniform sampler2D RopePositionsTextureSDF;

	` +scDeclareSceneUniformBuffer()+/* glsl */`
	`+scGetCommonFuncsDeclarations()+ /* glsl */`
	`+scGetSDFFuncsDeclarations(sceneSDFFunc)+ /* glsl */`

	

	float sdCone( in vec3 pointPos, in vec3 conePos, in vec2 c, float h )
	{
		vec3 p = pointPos - conePos;
    	vec2 q = h*vec2(c.x,-c.y)/c.y;
    	vec2 w = vec2( length(p.xz), p.y );
		
		vec2 a = w - q*clamp( dot(w,q)/dot(q,q), 0.0, 1.0 );
    	vec2 b = w - q*vec2( clamp( w.x/q.x, 0.0, 1.0 ), 1.0 );
    	float k = sign( q.y );
    	float d = min(dot( a, a ),dot(b, b));
    	float s = max( k*(w.x*q.y-w.y*q.x),k*(w.y-q.y)  );
		return sqrt(d)*sign(s);
	}

	float bayer2(vec2 a){
		a = floor(a);
		return fract( dot(a, vec2(.5, a.y * .75)) );
	}
	#define bayer4(a)   (bayer2( .5*(a))*.25+bayer2(a))
	#define bayer8(a)   (bayer4( .5*(a))*.25+bayer2(a))
	#define bayer16(a)  (bayer8( .5*(a))*.25+bayer2(a))
	#define bayer32(a)  (bayer16(.5*(a))*.25+bayer2(a))
	#define bayer64(a)  (bayer32(.5*(a))*.25+bayer2(a))
	#define bayer128(a) (bayer64(.5*(a))*.25+bayer2(a))

	void main()
	{
		vec3 rayOrigin = SceneBuffer.CameraPosition;
		rayOrigin.z -= 0.1;

		vec3 color = vec3(0.0);
		

		//NDC to world
		vec2 posNDC = MapToRange(vsOutTexCoords, 0.0, 1.0, -1.0, 1.0);
		vec3 posWS = vec3(posNDC.x * SceneBuffer.ScreenRatio, posNDC.y, 0.0);
		posWS.xy /= SceneBuffer.CameraZoom;
		const float farPlane = 20.0;
		posWS.xy *= farPlane;
		posWS.xy += SceneBuffer.CameraPosition.xy;
		posWS.z = SceneBuffer.CameraPosition.z + farPlane;

		vec3 rayDir = normalize(posWS - rayOrigin);

		////////////////
		//	SDF
		///////////////
		bool bHit = false;
		vec3 hitPositionWS = rayOrigin;
		// March along the ray until we hit something or reach maximum distance
		float t = 0.01;
		int numSteps = 0;

		bool bRayMeshIntersection = false;
		{
			vec4 rayMeshIntersectionRes = RayAABBIntersection(rayOrigin, rayDir, SceneBuffer.SDFMeshBoundsCenter, SceneBuffer.SDFMeshBoundsExtent);
			bRayMeshIntersection = rayMeshIntersectionRes.x > 0.0;
			/* if(bRayMeshIntersection)
			{
				t = rayMeshIntersectionRes.z;
			} */
		}

		////////
		//Main
		////////
		SDFMarchResult payload = MarchSDFScene(rayOrigin, rayDir);
		bHit = payload.bHit;
		hitPositionWS = payload.HitPosWS;
		t = payload.tRes;
		numSteps = payload.NumSteps;

		float depth = clamp(t / 10.0, 0.0, 1.0);

		vec3 normal = vec3(0.0, 0.0, -1.0);
		normal = calcNormal(hitPositionWS, true, true);

		float occ = 1.0;
		float ran = hash( uint(gl_FragCoord.x) + 1920U* uint(gl_FragCoord.y) + (1920U*1080U) );  
		occ = CalcAOPrecise( hitPositionWS, normal );
		/* occ *= CalcAOSpread( hitPositionWS, normal, ran );
		occ *= CalcAOSpreadHemisphere( hitPositionWS, normal, 1., 0.5 ); */

		/* hitPositionWS.x = clamp(hitPositionWS.x, -3.5, 3.5); 
		hitPositionWS.y = clamp(hitPositionWS.y, -2.5, 2.5);  */
		
		
		if(bHit)
		{
			//color = vec3(1.0 - depth);
			//color = vec3(0.1);
			color = vec3(1.0, 0.5, 0.1);
			//color *= (1.0 - depth);
			//color = vec3(1.0);
			//color =  -normal;
			color *= occ;

			float NdotV = 1.0 - max(dot(normal, vec3(0.0, 0.0, -1.0)), 0.0);

			color *= NdotV;

			//Shade
			{
				//vec3 lightpos = vec3(0.0, 1.0, -5.0);
				vec3 lightpos = SceneBuffer.SpotlightPos2;
				vec3 l = normalize(lightpos - hitPositionWS);
				float distanceToCurLight = length(lightpos - hitPositionWS);
				float NdotL = max(dot(normal, l), 0.0);
				float ssThickness = 0.0;
				float sssRes = 0.0;
				#if 0
				//SSS
				{
					//compute thickness
					const float sstMin = 0.01;
					float sst = sstMin;
					vec3 ssrdir = vec3(0.0, 0.0, 1.0);
					//ssrdir = l;
					for( int i=0; i<32; i++ )
					{
						vec3 ssp = hitPositionWS + ssrdir * sst;

						float h = sceneSDF(ssp, true);

						if(h < 0.0)
						{
							ssThickness += 0.01;
						}

						sst += 0.01;
					}

					//color += (1.0 - min(1.0, ssThickness));
					sssRes = (1.0 - min(1.0, ssThickness));
					/* float wrap = sssRes;
					NdotL = max(0.0, (NdotL + wrap) / (1.0 + wrap)); */
				}
				#endif

				
				//NdotL = 1.0;
				//occ = 1.0;
				float lightShadow = SDFComputeShadow(hitPositionWS, lightpos);\
				float attenuation = clamp(1.f - pow(distanceToCurLight / 20.0, 2.0), 0.f, 1.f);
				color += vec3(1.0, 1.0, 0.8) * NdotL * attenuation * occ /* * lightShadow */;
				//color = vec3(1.0, 1.0, 0.8)  * attenuation  * lightShadow;
			}

			color *= 0.3;

			float stepsVis = float(numSteps) / float(SDF_MAX_STEPS);
			stepsVis *= stepsVis;
			//color = vec3(stepsVis);

		#if 0
			float edge = 0.0;
			{
				SDFMarchResult payloadT;
				const float offset = 0.001;
				const bool bMarchMesh = true;
				payloadT = MarchSDFScene(rayOrigin, rayDir + vec3(offset, 0.0, 0.0));
				edge += length(normal - calcNormal(payloadT.HitPosWS, bMarchMesh, bMarchMesh));
				payloadT = MarchSDFScene(rayOrigin, rayDir + vec3(0.0, offset, 0.0));
				edge += length(normal - calcNormal(payloadT.HitPosWS, bMarchMesh, bMarchMesh));
			}
			color = vec3(edge) * 0.5 * vec3(1.0, 0.75, 0.1);
		#endif

		}
		else
		{
			color = vec3(1.0, 0.0, 1.0) * 0.3;
		}

		color = max(vec3(0.0), normal) * 0.85; 

		
		outColor = color;
	

	}`;
}


export function GetShaderSourceSDFVolumeLightRenderPS(sceneSDFFunc : string) {
	return /* glsl */ `#version 300 es
	
	precision highp float;
	precision highp sampler2D;

	layout(location = 0) out vec3 outColor;

	in vec2 vsOutTexCoords;

	uniform sampler2D ProjectorTexture;
	uniform sampler2D BlueNoiseTexture;
	uniform highp sampler3D SDFTexture;
	uniform highp sampler3D SDFTexture2;
	uniform sampler2D RopePositionsTextureSDF;

	` +scDeclareSceneUniformBuffer()+/* glsl */`
	`+scGetCommonFuncsDeclarations()+ /* glsl */`
	`+scPBRDeclarations()+ /* glsl */`
	`+scGetSDFFuncsDeclarations(sceneSDFFunc)+ /* glsl */`
	
	void main()
	{
		vec3 rayOrigin = SceneBuffer.CameraPosition;
		rayOrigin.z -= 0.1;

		vec3 color = vec3(0.0);

		//NDC to world
		vec2 posNDC = MapToRange(vsOutTexCoords, 0.0, 1.0, -1.0, 1.0);
		vec3 posWS = vec3(posNDC.x * SceneBuffer.ScreenRatio, posNDC.y, 0.0);
		posWS.xy /= SceneBuffer.CameraZoom;
		const float farPlane = 20.0;
		posWS.xy *= farPlane;
		posWS.xy += SceneBuffer.CameraPosition.xy;
		posWS.z = SceneBuffer.CameraPosition.z + farPlane;

		vec3 rayDir = normalize(posWS - rayOrigin);

		vec3 volumeCurPos = rayOrigin;
		const float marchLength = 10.0;
		const float marchStepSize = marchLength * 0.025;
		float tt = 0.0;
		vec3 scatter = vec3(0.0);
		float transmittance = 1.0;


		//start marching from the scene bbox start
		float sceneNearPlaneZ = SceneBuffer.SceneBoundsCenter.z + SceneBuffer.SceneBoundsExtent.z;
		if(rayOrigin.z < sceneNearPlaneZ)
		{
			float sdfNearPlane = abs(rayOrigin.z - sceneNearPlaneZ);
			float advT = sdfNearPlane / rayDir.z;
			//rayOrigin = rayOrigin + advT * rayDir;
			//rayOrigin = rayOrigin + sdfNearPlane * rayDir;
		}

		vec2 pixelPos = vec2(gl_FragCoord.xy);
		float jitter = textureLod(BlueNoiseTexture, pixelPos / 512.0 /* uvOffset + vsOutTexCoords * vec2(SceneBuffer.ScreenRatio, 1.0) * 1.0 */, 0.0).r;
		const float kGoldenRatioConjugate = 0.61803398875f;
        //jitter = fract(jitter + mod(SceneBuffer.Time * 100.0, 64.0) * kGoldenRatioConjugate);
		jitter = fract(jitter + hashf(SceneBuffer.Time) * 0.1);


		//float jitter = smoothNoise(rayDir);
		float rayStartOffset = jitter;
		for (float tt = rayStartOffset; tt < marchLength; ) {

			float od = marchStepSize;

			volumeCurPos = rayOrigin + tt * rayDir;

			#if 0 //Flashlight
			{
				vec3 lightColor = vec3(1.0, 1.0, 1.0);
				vec3 lightPos = SceneBuffer.SpotlightPos;
				//lightPos = SceneBuffer.CameraPosition;
				vec3 lightTargetPos = SceneBuffer.SpotlightTargetPos;
				vec3 p = volumeCurPos;
				float d = length(lightPos - p);
				vec3 l = (lightPos - p) / d;
				float SpotlightFalloff = GetSpotlightFalloff(lightPos, normalize(lightTargetPos - lightPos), volumeCurPos, SceneBuffer.SpotlightOuterAngle, SceneBuffer.SpotlightInnerAngleOffset);
				scatter += lightColor * od * SpotlightFalloff * 0.05;
			}
			#endif

			#if 0 //Spotlight
			{
				vec3 lightColor = vec3(1.0, 1.0, 1.0);
				vec3 lightPos = SceneBuffer.SpotlightPos2;
				vec3 lightTargetPos = SceneBuffer.SpotlightTargetPos2 - vec3(0.0, 0.5, 0.0);
				vec3 p = volumeCurPos;
				float d = length(lightPos - p);
				vec3 l = (lightPos - p) / d;
				float SpotlightFalloff = GetSpotlightFalloff(lightPos, normalize(lightTargetPos - lightPos), volumeCurPos, 1.0, 0.75);
				#if 0 //occlusion
				{
					float occlusion = 1.0;
					
					//cone
					{
						float minT = 0.1;
						float maxT = d;
						float t = minT;

						for( int i=0; i<32; i++ )
						{
							float h = SDF_MAX_DISTANCE;
							vec3 curPos = p + l*t;
							{
								const float handleRad = 0.1;
								h = min(h, sdCappedCone(curPos,
									 SceneBuffer.LightSwitchHandlePos + vec3(0.0, 0.35, 0.0),
									 SceneBuffer.LightSwitchHandlePos - vec3(0.0, 0.35, 0.0), 
									 handleRad * 0.7,
									handleRad));
							}

							t += h;

							if ((h)<(0.001*t))
							{
								occlusion *= 0.0;
								break; 
							}
							else if( t > maxT)
							{
								break;
							}
						}
					}

					#if 0//Rope
					{
						int numInstances = int(SceneBuffer.SwitchRopeNumSpheres);
						float radius = SceneBuffer.SwitchRopeSphereRadius * 2.0;
						for(int i = 0; i < numInstances; i++)
						{
							int offset = i * 3;
							vec3 instancePos = vec3(
								texelFetch(RopePositionsTextureSDF, ivec2(offset + 0, 0), 0).r,
								texelFetch(RopePositionsTextureSDF, ivec2(offset + 1, 0), 0).r,
								texelFetch(RopePositionsTextureSDF, ivec2(offset + 2, 0), 0).r
							);
							bool bIntersection = RaySphereIntersection(p, l, instancePos, radius);
							if(bIntersection)
							{
								occlusion *= 0.0;
								break; 
							}
						}
					}
					#endif
					SpotlightFalloff *= occlusion;
				}
				#endif
				SpotlightFalloff *= SpotlightFalloff;
				float att = GetLightAttenuation(d, SceneBuffer.PointLightRadius1 * 2.0);
				//att = 1.0;
				scatter += lightColor * od * SpotlightFalloff * att * 0.75;
			}
			#endif

			#if 1
			//projector
			{
				//get projector space pos
				vec3 posProjSpace = (vec4(volumeCurPos, 1.0) * SceneBuffer.WorldToProjectorSpaceMat).xyz;
				posProjSpace.xy *= SceneBuffer.ProjectorZoom;
				posProjSpace.z += 0.01;
				float divisor = posProjSpace.z;
				vec2 projNDCPos = vec2(posProjSpace.x / divisor, posProjSpace.y / divisor);
				projNDCPos.y *= SceneBuffer.ProjectorXYRatio;
				vec2 projUVPos = MapToRange(projNDCPos, -1.0, 1.0, 0.0, 1.0);
				vec3 projectorColor = SceneBuffer.ProjectorColor * SceneBuffer.ProjectorIntensity;
				
				if(projUVPos.x < 1.0 && projUVPos.x > 0.0 && projUVPos.y > 0.0 && projUVPos.y < 1.0 && posProjSpace.z > 0.0)
				{
					const highp float rectPow = 32.f;
					highp float rectCircleLength = pow(abs(projNDCPos.x), rectPow) + pow(abs(projNDCPos.y), rectPow);
					float projMask = 1.0;
					if(rectCircleLength < 0.8)
					{
						projMask = 1.0;
					}
					else
					{
						projMask = 0.0;
					}

					#if 0
					vec2 uvInv = vec2(projUVPos.x, 1.0 - projUVPos.y);
					#else
					vec2 uvInv = projUVPos;
					#endif
					vec3 projTexture = texture(ProjectorTexture, uvInv.xy).rgb;
					projectorColor *= (vec3(0.01) + projTexture) * SceneBuffer.ProjectorIntensityVolume;
					projectorColor *= projMask;

					float ProjectorFalloff = SceneBuffer.ProjectorFalloffVolume;
					float d = posProjSpace.z;
					float num = pow(clamp(1.0 - pow(d /  ProjectorFalloff, 4.0), 0.0, 1.0), 2.0);
					float denom =  (d * d + 1.0);
					float attenuation = num / denom;

					scatter += projectorColor * od * attenuation /* * transmittance */;
				}
			}
			#endif

			#if 0
			//sdf mesh
			{
				vec3 sdfMeshBoxExtent = vec3(1.0, 1.0, 1.0);
				vec3 sdfMeshBoxCenter = vec3(0.0, 0.0, -4.0);
				vec3 sdfMeshBoxMin = sdfMeshBoxCenter - sdfMeshBoxExtent;
				vec3 sdfMeshBoxMax = sdfMeshBoxCenter + sdfMeshBoxExtent;
				{
					//sample SDF texture
					if(sdBox(volumeCurPos, sdfMeshBoxCenter, sdfMeshBoxExtent) < 0.1)
					{
						//we are inside mesh volume
						//map world pos to UVW space
						vec3 uvw = MapToRange(volumeCurPos, sdfMeshBoxMin, sdfMeshBoxMax, vec3(0.0), vec3(1.0));
						//uvw.z = 1.0 - uvw.z;
						//curD = min(curD, textureLod(SDFTexture, uvw, 0.0).r);
						float dToL = texture(SDFTexture, uvw).r;
						/* dToL = max(0.2, dToL);
						scatter += dToL * 0.3 * od; */
						if(dToL < 0.01)
						{
							scatter += 0.25 * od;
						}
						else
						{
							scatter += 0.025 * od;
						}
					}
				}
			}
			#endif

			//decay for distance to observer
			transmittance *= exp2(-od * 0.1); //exp decay

			//jitter = hashf(rayDir.x + rayDir.y + tt);
			//jitter = 0.0;
			//tt += marchStepSize + jitter * marchStepSize;
			tt += marchStepSize;

		}

		color = scatter;

		outColor = color;

	}
	
	`;
}


export function GetShaderSourceDestrSceneVolumeLightRenderPS(numTiles : Vector2, tileSize : Vector2, sceneSDFFunc : string) {
	return /* glsl */ `#version 300 es
	
	precision highp float;
	precision highp sampler2D;

	layout(location = 0) out vec3 outColor;

	in vec2 vsOutTexCoords;

	uniform sampler2D ProjectorTexture;
	uniform sampler2D BlueNoiseTexture;

	` +scDeclareSceneUniformBuffer()+/* glsl */`
	`+scGetCommonFuncsDeclarations()+ /* glsl */`
	`+scPBRDeclarations()+ /* glsl */`
	`+scGetSDFFuncsDeclarations(sceneSDFFunc)+ /* glsl */`

	`+scGetDestrTileCommonFuncsDecl(numTiles, tileSize)+ /* glsl */`
	
	void main()
	{
		vec3 rayOrigin = SceneBuffer.CameraPosition;
		rayOrigin.z -= 0.1;

		vec3 color = vec3(0.0);

		//NDC to world
		vec2 posNDC = MapToRange(vsOutTexCoords, 0.0, 1.0, -1.0, 1.0);
		vec3 posWS = vec3(posNDC.x * SceneBuffer.ScreenRatio, posNDC.y, 0.0);
		posWS.xy /= SceneBuffer.CameraZoom;
		const float farPlane = 20.0;
		posWS.xy *= farPlane;
		posWS.xy += SceneBuffer.CameraPosition.xy;
		posWS.z = SceneBuffer.CameraPosition.z + farPlane;

		vec3 rayDir = normalize(posWS - rayOrigin);

		vec3 volumeCurPos = rayOrigin;
		const float marchLength = 5.0;
		const float marchStepSize = marchLength * 0.025 * 0.25;
		float tt = 0.0;
		vec3 scatter = vec3(0.0);
		float transmittance = 1.0;


		vec2 pixelPos = vec2(gl_FragCoord.xy);
		float jitter = textureLod(BlueNoiseTexture, pixelPos / 512.0 /* uvOffset + vsOutTexCoords * vec2(SceneBuffer.ScreenRatio, 1.0) * 1.0 */, 0.0).r;
		const float kGoldenRatioConjugate = 0.61803398875f;
        //jitter = fract(jitter + mod(SceneBuffer.Time * 100.0, 64.0) * kGoldenRatioConjugate);
		jitter = fract(jitter + hashf(SceneBuffer.Time) * 0.1);

		//jitter = 0.0;

		//float jitter = smoothNoise(rayDir);
		float rayStartOffset = jitter;
		for (float tt = rayStartOffset; tt < marchLength; ) {

			float od = marchStepSize;

			volumeCurPos = rayOrigin + tt * rayDir;

			#if 1 //Spotlight
			{
				vec3 lightColor = vec3(1.0, 1.0, 1.0);
				vec3 lightPos = SceneBuffer.SpotlightPos2;
				vec3 lightTargetPos = lightPos + SceneBuffer.SpotlightTargetPos2;
				vec3 p = volumeCurPos;
				float d = length(lightPos - p);
				vec3 l = (lightPos - p) / d;

				//float SpotlightFalloff = GetSpotlightFalloff(lightPos, normalize(lightTargetPos - lightPos), volumeCurPos, 1.0, 0.75);
				float SpotlightFalloff = GetSpotlightFalloff(lightPos, normalize(lightTargetPos - lightPos), volumeCurPos, 0.85, 0.15);
				
				//SpotlightFalloff *= SpotlightFalloff;

				float att = GetLightAttenuation(d, SceneBuffer.PointLightRadius1 * 2.0);
				att *= att;
				//att = 1.0;
				scatter += lightColor * od * SpotlightFalloff * att * 0.75;
			}
			#endif

			#if 1 //Direct Light
			if(volumeCurPos.z < 0.01)
			{
				vec3 lightDir = normalize(vec3(1.0, -1.0, -0.5));

				vec3 planeCenterWS = vec3(0.0, 0.0, 0.001 * 0.5);
				vec3 planeExtentWS = vec3(2.0, 2.0, 0.001);

				vec4 payload = RayAABBIntersection(volumeCurPos, -lightDir, planeCenterWS, planeExtentWS);
				bool bHit = payload.x > 0.0;

				if(bHit)
				{
					vec3 p = volumeCurPos - lightDir * payload.z;

					ivec3 curTileId = GetTileIdFromPosWS(p);

					if(curTileId.z > 0)
					{
						float health = texelFetch(ProjectorTexture, curTileId.xy, 0).r;
						if(health < kTileHealthThres)
						{
							scatter += vec3(1.0) * 0.01 * 2.0;
						}
					}
				}
				

			}
			#endif


			//decay for distance to observer
			transmittance *= exp2(-od * 0.1); //exp decay

			//jitter = hashf(rayDir.x + rayDir.y + tt);
			//jitter = 0.0;
			//tt += marchStepSize + jitter * marchStepSize;
			tt += marchStepSize;

		}

		color = scatter;

		outColor = color;

	}
	
	`;
}




export function GetShaderSourceHallwayPostProcessPS(sceneSDFFunc : string) {
	return /* glsl */ `#version 300 es
	
	precision highp float;
	precision highp sampler2D;

	layout(location = 0) out vec4 outColor;

	in vec2 vsOutTexCoords;

	uniform sampler2D ProjectorTexture;
	uniform sampler2D BlueNoiseTexture;
	uniform highp sampler3D SDFTexture;
	uniform highp sampler3D SDFTexture2;
	uniform sampler2D RopePositionsTextureSDF;

	` +scDeclareSceneUniformBuffer()+/* glsl */`
	`+scGetCommonFuncsDeclarations()+ /* glsl */`
	`+scPBRDeclarations()+ /* glsl */`
	`+scGetSDFFuncsDeclarations(sceneSDFFunc)+ /* glsl */`
	
	//#undef SDF_MAX_DISTANCE
	// #define SDF_MAX_DISTANCE 100.0


	void main()
	{
		vec3 rayOrigin = SceneBuffer.CameraPosition;
		rayOrigin.z -= 0.1;
		vec3 rayDir;

		vec3 color = vec3(0.8, 0.1, 0.0) * 0.0;
		

		//NDC to world
		{
			vec2 posNDC = MapToRange(vsOutTexCoords, 0.0, 1.0, -1.0, 1.0);
			vec3 posWS = vec3(posNDC.x * SceneBuffer.ScreenRatio, posNDC.y, 0.0);
			posWS.xy /= SceneBuffer.CameraZoom;
			const float farPlane = 20.0;
			posWS.xy *= farPlane;
			posWS.xy += SceneBuffer.CameraPosition.xy;
			posWS.z = SceneBuffer.CameraPosition.z + farPlane;
			rayDir = normalize(posWS - rayOrigin);
		}

		{
			vec3 distOffset = vec3(0.0); 
			distOffset.xy = fbm22LQ(vec2(0.1, 1.0) * SceneBuffer.Time * 0.2);
			rayDir = normalize(rayDir + distOffset * 0.1 * 0.1);
			rayOrigin = rayOrigin - distOffset * 0.3;
		}

		
		vec3 light = vec3(0.0);
		bool bEmitter = false;
		
		vec3 hitPositionWS = rayOrigin;
		float t = SDF_MAX_DISTANCE;

		////////
		//Main
		////////
		//Raymarch Main
		if(rayOrigin.z < (HALLWAY_EXT_Z + 5.0))
		{
			SDFMarchResult payload = MarchSDFScene(rayOrigin, rayDir);
			hitPositionWS = payload.HitPosWS;
			t = payload.tRes;
		}

		//Red Line
		#if 1
		if(rayOrigin.z < (HALLWAY_EXT_Z + 5.0))
		{
			float lineT = 0.01;
			
			for( int i=0; i<512; i++)
			{
				vec3 pos = rayOrigin + lineT * rayDir;

				if(pos.z > HALLWAY_EXT_Z)
				{
					break;
				}

				pos.xy += fbm22(pos.xy) * 0.2;

				const float lineRad = 0.055 * 0.8;
				float h = sdPipe(pos, vec3(RedLineFunc(pos.z), -3.75, lineRad));
				if(h<(0.0001 * lineT)) 
				{
					light = vec3(1.0, 0.2 * 0.5, 0.01) * 3.0 * 1.5;
					bEmitter = true;
					hitPositionWS = pos;
					t = lineT;
					break;
				}
				if(lineT>100.0)
				{
					break;
				}
				lineT += h * 0.95;
			}
		}
		#endif

		//if(t < 100.0)
		{
			bool bUnderwater = false;
			//Post-process
			float curCamWaterLevel = sea_map_detailed(rayOrigin + rayDir * 0.25);
			
			if(curCamWaterLevel < 0.1)
			{
				bUnderwater = true;

				vec3 waterColor = vec3(0.0);

				//check TV intersection

				const vec3 tvPos = vec3(0.0, -17.0, 175.0);
				const float tvSize = 15.0;
				const vec3 tvExt = vec3(tvSize * 1.5, tvSize, 0.01);
				
				{
					vec3 tvDirVec = rayDir;

					vec3 distOffset = vec3(0.0); 
					distOffset.xy = fbm22((rayDir).xy + vec2(0.1, 1.0) * SceneBuffer.Time * 0.4);

					tvDirVec = normalize(tvDirVec + distOffset * 0.1 * 0.4);

					vec4 rayTVInterTest = RayAABBIntersection(rayOrigin, tvDirVec, tvPos, tvExt);
					if(rayTVInterTest.x > 0.0)
					{
						//tvT = rayTVInterTest.y;

						vec3 tvHitPos = rayOrigin + tvDirVec * rayTVInterTest.y;

						vec2 tvUV;
						tvUV.x = MapToRange(tvHitPos.x, tvPos.x - tvExt.x, tvPos.x + tvExt.x, 0.0, 1.0);
						tvUV.y = MapToRange(tvHitPos.y, tvPos.y - tvExt.y, tvPos.y + tvExt.y, 0.0, 1.0);

						#if 1
						vec4 tvColor = GetTVColor(tvUV, rayOrigin.z);
						waterColor += tvColor.rgb * 20.0;

						if(tvColor.a > 0.0)
						{
							t = rayTVInterTest.y;
						}
						#endif

					}
				}

				float camTransParam = MapToRangeClamped(rayOrigin.y, -2.5, -15.0, 0.0, 1.0);

				/////////////////////
				float marchMaxLength = 50.0;
				const int numMarchSamples = 32;
				float marchStepSize = marchMaxLength / float(numMarchSamples);
		
				vec3 scatter = vec3(0.0);
		
				float transmittance = 1.0;
				float sigmaE = 0.0;//extinction
				float sigmaS = 0.0;//out-scatter

				const float p = 1.0/(4.0*PI);

				float jitter = whangHashNoise(uint(gl_FragCoord.x) + 2000u, uint(gl_FragCoord.y) + 5000u, uint(SceneBuffer.Time * 30.0)*12345u );
				for(int i = 0; i < numMarchSamples; i++)
				{
					float tt = 0.25 + jitter + marchStepSize * float(i + 1);

					if(tt > t)
					{
						break;
					}
		
					vec3 rayMarchCurPos = rayOrigin + tt * rayDir;

					float newCamWaterLevel = sea_map(rayMarchCurPos);

					if(newCamWaterLevel > 0.01)
					{
						float minThick = 0.0;
						float waterThickness = clamp(MapToRange(rayMarchCurPos.y, SEA_POS_Y, SEA_POS_Y + SEA_HEIGHT + 1.0, minThick, 1.0), minThick, 1.0);
						waterThickness = 1.0 - waterThickness;
						waterThickness *= waterThickness;
						vec3 lightScatter = vec3(1.0, 0.0, 0.0) * waterThickness;

						vec3 distVec = rayMarchCurPos - rayOrigin;
						vec3 N = getNormal(rayMarchCurPos - vec3(0.0, 0.1, 0.0), dot(distVec,distVec) * 0.0015);

						const vec3 pyrPos = vec3(0.0, 41.0, 200.0);
						const vec3 lightPos = pyrPos - vec3(0.0, -2.0, 0.0);
					
						float d = length(lightPos - rayMarchCurPos);
						vec3 l = (lightPos - rayMarchCurPos) / d;
						float NdotL = max(dot(N, l), 0.0);

						float LdotV = max(dot(N, rayDir), 0.0);

						waterColor = vec3(0.0);
						float closestDist;
						float reflPyrT;

						vec2 dirOffset = fbm22(rayMarchCurPos.xz + vec2(0.2, 0.5) * SceneBuffer.Time * 3.0) * 0.25;
						vec3 hitDir = normalize(rayDir + vec3(dirOffset.x, 0.0, dirOffset.y));
						//hitDir.y = abs(hitDir.y);

						//hitDir = rayDir;
						
						if(bPyramidIntersection(rayOrigin, hitDir, closestDist, reflPyrT))
						{
							const float pyrExtent = 65.0;

							waterColor = vec3(1.0, 0.0, 0.0);
							vec3 hitPos = rayOrigin + hitDir * reflPyrT;
							float ltoc = 0.01 * length(hitPos - pyrPos - vec3(0.0, pyrExtent * -0.25, 0.0));
							ltoc = 1.0 - ltoc;
							ltoc = clamp(ltoc, 0.0, 1.0);
							ltoc *= ltoc;
							ltoc *= ltoc;
							ltoc *= ltoc;
							waterColor *= ltoc * 0.1 * SceneBuffer.PyramidBrightness;

						}
						waterColor += vec3(1.0, 0.0, 0.0) * lightScatter * NdotL * LdotV;
						waterColor *= mix(0.5, 1.0, camTransParam);
						break;
					}

					float sigmaS = 0.15;

					float lightShaft = fbm2((rayMarchCurPos.xz - rayDir.zy) * 3.0 + vec2(0.2, 0.5) * SceneBuffer.Time) * 3.0;

					vec3 curLight = 0.15 * (1.0 + lightShaft * 0.5) * 0.75 * vec3(1.0, 0.1, 0.0) * max(0.0, 1.0 - (abs(rayMarchCurPos.y - SEA_POS_Y) * 0.2)) * clamp(SceneBuffer.PyramidBrightness * 0.5, 1.0, 6.0);

					curLight += vec3(0.0, 0.25, 0.25) * 0.5;

					scatter += sigmaS * curLight * p * transmittance * marchStepSize * mix(0.2, 1.0, camTransParam);

					transmittance *= exp(-sigmaS * marchStepSize);
				}

				vec3 underCol = waterColor * transmittance + scatter;

				float curCamWaterLevelUp = sea_map_detailed(rayOrigin + rayDir * 0.25 + vec3(0.0, 0.006, 0.0));
				if(curCamWaterLevelUp > 0.1)
				{
					float ws = max(0.0, MapToRange(curCamWaterLevelUp, 0.1, 0.105, 1.0, 0.0));
					underCol *= ws * ws;
				}

				//////////////////////

				outColor = vec4(underCol, 1.0);

				return;
			}
			
			const float pyrExtent = 65.0;
			const float pyrScale = 1.0 / pyrExtent;
			const vec3 pyrPos = vec3(0.0, 41.0, 200.0);

			#if 1 //PYRAMID SDF
			if(!bUnderwater)
			{
			bool bPyrInter = false;

			vec4 rayPayramidIntersectionRes = RayAABBIntersection(rayOrigin, rayDir, pyrPos - vec3(0.0, pyrExtent * 0.5, 0.0), vec3(pyrExtent * 0.5 /* * 0.5 * 1.25 */));
			bPyrInter = rayPayramidIntersectionRes.x > 0.0 && rayPayramidIntersectionRes.y < t - 0.1;

			if(bPyrInter)
			{
				/* outColor = vec4(0.0, 0.0, 1.0, 1.0);
				return; */

				// raymarch
				const float pyrtmax = 500.0;
				float pyrt = rayPayramidIntersectionRes.y;
				for( int i=0; i<1024; i++ )
				{
					vec3 pos = rayOrigin + pyrt*rayDir;
					float h = sdPyramid(pos, pyrPos, pyrScale, pyrHeight);
					if(h < (0.00001 * pyrt + 0.00002 * float(i)) ) 
					{
						//outColor = vec4(1.0, 0.0, 0.0, 1.0); return;

						pyrt += h;
						pos = rayOrigin + pyrt*rayDir;

						//compute normal
						vec3 pyrN = calcNormalPyr(pos);

						//compute thickness
						#if 0
						float thickness = CalcPyrThickness(pos, pyrN);
						thickness *= thickness;
						thickness *= thickness;
						thickness *= thickness;
						thickness *= thickness;
						thickness *= 1.2;
						#else
						float thickness = 1.0 - CalcPyrThicknessHemisphere(pos, pyrN);
						thickness *= thickness;
						thickness *= thickness;
						thickness *= thickness;
						thickness *= thickness;
						thickness *= 2.6;
						#endif

						//thickness = 0.0;
						float ltoc = 0.01 * length(pos - pyrPos - vec3(0.0, pyrExtent * -0.25, 0.0));
						ltoc = 1.0 - ltoc;
						ltoc = clamp(ltoc, 0.0, 1.0);
						ltoc *= ltoc;
						ltoc *= ltoc;
						ltoc *= ltoc;
						//thickness += ltoc * thickness * 4.0;
						thickness *= ltoc;
						//thickness *= 1.3;

						//light = vec3(1.0, 0.01, 0.01) * 7.0 * thickness;
						light = vec3(1.0, 0.02, 0.01) * 40.0 * thickness * SceneBuffer.PyramidBrightness * 0.8;

						bEmitter = true;
						hitPositionWS = pos;
						t = pyrt;

						

						break;
					}
					if(pyrt>pyrtmax)
					{
						break;
					}
					pyrt += h * (0.95 + min(0.05, 0.01 * float(i)));
				}
			}

			}
			#endif//PYRAMID SDF

			


			#if 1//OCEAN
			if(!bUnderwater && hitPositionWS.z > HALLWAY_EXT_Z)
			{
				// now ray.y must be negative, water must be hit
  				// define water planes
  				const vec3 waterPlaneHigh = vec3(0.0, SEA_POS_Y + SEA_HEIGHT * 3.0, 0.0);
  				const vec3 waterPlaneLow = vec3(0.0, SEA_POS_Y, 0.0);

  				// calculate intersections and reconstruct positions
  				float highPlaneHit = RayPlaneIntersectionWorldSpace(rayOrigin, rayDir, vec3(0.0, 1.0, 0.0), SEA_POS_Y + SEA_HEIGHT * 3.0);
  				//float lowPlaneHit = RayPlaneIntersectionWorldSpace(rayOrigin, rayDir, vec3(0.0, 1.0, 0.0), SEA_POS_Y);
				highPlaneHit = abs(highPlaneHit);

				float rayDirCeilVal = MapToRangeClamped(rayOrigin.y, -2.5, -4.5, 0.0, 0.2);

				if(rayDir.y < rayDirCeilVal)
				//if(highPlaneHit > 0.0f || lowPlaneHit > 0.0f)
				{
					vec3 waterHitPos;
					heightMapTracing(rayOrigin,rayDir, max(0.0, highPlaneHit * MapToRangeClamped(rayOrigin.y, -2.5, -5.0, 1.0, 0.0)), waterHitPos);
					vec3 distVec = waterHitPos - rayOrigin;
					vec3 N = getNormal(waterHitPos, dot(distVec,distVec) * 0.00015);
					float dist = length(distVec);
					if(dist < (t - 0.01))
					{

					//outColor = vec4(1.0, 0.0, 0.0, 1.0); return;
					
  					// smooth the normal with distance to avoid disturbing high frequency noise
  					vec3 reflN = mix(N, vec3(0.0, 1.0, 0.0), 0.8 * min(1.0, sqrt(dist*0.01) * 1.1));
					reflN = N;

					// reflect the ray and make sure it bounces up
  					vec3 R = normalize(reflect(rayDir, reflN));
  					//R.y = abs(R.y);

					float minThick = -0.5;
					float waterThickness = clamp(MapToRange(waterHitPos.y, SEA_POS_Y, SEA_POS_Y + SEA_HEIGHT * 5.0 + 1.0, minThick, 1.0), minThick, 1.0);
					//waterThickness *= waterThickness;

					#if 1//Pyramid shading
					{
						const vec3 pyrColor = vec3(1.0, 0.02, 0.01);

						vec3 lightReflected = vec3(0.0);
						
						float closestDist;
						float reflPyrT;
						if(bPyramidIntersection(waterHitPos, R, closestDist, reflPyrT))
						{
							lightReflected = pyrColor;
							
							vec3 hitPos = waterHitPos + R * reflPyrT;
							float ltoc = 0.01 * length(hitPos - pyrPos - vec3(0.0, pyrExtent * -0.25, 0.0));
							ltoc = 1.0 - ltoc;
							ltoc = clamp(ltoc, 0.0, 1.0);
							ltoc *= ltoc;
							ltoc *= ltoc;
							ltoc *= ltoc;
							lightReflected *= ltoc * 100.0;
						}
						else if(closestDist < 1.0)
						{
							float cd = max(0.0, 1.0 - closestDist);
							cd *= cd;
							cd *= cd;
							cd *= cd;
							lightReflected = pyrColor * cd;
						}
					
						const vec3 pyrPos = vec3(0.0, 41.0, 200.0);
						const vec3 lightPos = pyrPos - vec3(0.0, -2.0, 0.0);
					
						float d = length(lightPos - waterHitPos);
						vec3 l = (lightPos - waterHitPos) / d;
						float NdotL = max(dot(N, l), 0.0);
						const float softness = 0.25;
						NdotL = MapToRange(NdotL, 0.0, 1.0, softness, 1.0);
					
						float attenuation = pow(clamp(1.f - pow(d / 150.0, 2.0), 0.f, 1.f), 2.0);
					
						float fresnel = (0.04 + (1.0-0.04)*(pow(1.0 - max(0.0, dot(-reflN, rayDir)), 4.0)));
						//fresnel = min(0.25, fresnel);

						lightReflected = fresnel * lightReflected * 0.125 * attenuation * NdotL;
						
						const vec3 baseSeaColor = vec3(1.0, 0.01, 0.0095);
						vec3 diffuse = baseSeaColor * attenuation * NdotL * 0.75;
					
						{
							vec3 lightScatter = vec3(1.0, 0.0, 0.0) * waterThickness * attenuation;
							diffuse += lightScatter * 0.75;
							diffuse = max(vec3(0.0), diffuse);
						}
						
						float dToCamAtt = pow(clamp(1.f - pow(length(vec3(0.0, 0.0, HALLWAY_EXT_Z) - waterHitPos) / 30.0, 2.0), 0.f, 1.f), 1.0);
						
						//light = (diffuse * 0.15 * 0.25 + lightReflected * 0.15) * 1.0 * clamp(SceneBuffer.PyramidBrightness, 2.0, 7.0) * 3.0 * clamp(SceneBuffer.PyramidBrightness - 0.2 + dToCamAtt, 0.0, 1.0);
						light = (diffuse * 0.15 * 0.375 + lightReflected * 0.1) * SceneBuffer.PyramidBrightness * 0.75 * 0.75;

						const float entranceLightScale = 3.0;

						light += vec3(1.0, 0.1, 0.01) * fresnel * 0.2 * dToCamAtt * max(0.25, SceneBuffer.PyramidParams.z);

						light += SceneBuffer.PyramidParams.z * entranceLightScale * (0.6 * (waterThickness + 0.5)) * GetLineLightShading(waterHitPos, N, R, -rayDir, vec3(1.0, 0.1, 0.0), vec3(0.0, -2.0 * 1.25, HALLWAY_EXT_Z + 10.0), vec3(0.0, -2.0 * 1.25, HALLWAY_EXT_Z), 10.0, 1.0);


						float curCamWaterLevelDown = sea_map_detailed(rayOrigin + rayDir * 0.25 - vec3(0.0, 0.0075, 0.0));
						if(curCamWaterLevelDown < 0.1)
						{
							float ws = max(0.0, MapToRange(curCamWaterLevelDown, 0.1, 0.095, 1.0, 0.0));
							light *= ws * ws;
						}

						{
							const float thres = 25.0;
							if(abs(waterHitPos.x) > thres)
							{
								float ws = clamp(MapToRange(abs(waterHitPos.x), thres, thres + 10.0, 1.0, 0.0), 0.0, 1.0);
								light *= ws;
							}
						}
						

					}
					#endif
					
					bEmitter = true;
					hitPositionWS = waterHitPos;
					t = length(hitPositionWS - rayOrigin);

					}
				}

  				
			}
			#endif

			if(!bEmitter)
			{
				PBRSettings s;

				s.Albedo = vec3(0.35);
				
				s.Roughness = 0.8;
				s.Metalness = 0.0;
				
				{
					float fbmNoise = fbm3(hitPositionWS * 3.3 * vec3(1.0, 1.0, 0.3));
					//s.Roughness = 0.2 + clamp(fbmNoise, 0.0, 1.0);
					//s.Albedo *= clamp(0.25 + Contrast(fbmNoise, 0.1), 0.0, 1.0);
				}

				s.Normal = vec3(0.0, 0.0, -1.0);
				s.Normal = calcNormal(hitPositionWS, false, false);

				#if 1 //noise bump
				{
					vec3 pos = hitPositionWS;
    				const float kke = 0.0001;
    				float bumpa = 0.0075;
					bumpa *= 0.75;
    				bumpa /= kke;
					const float prec = 32.0;
    				float kk = fbm3( 32.0*hitPositionWS );
    				s.Normal.x += bumpa*(fbm3( prec*vec3(pos.x+kke, pos.y, pos.z) )-kk);
    				s.Normal.y += bumpa*(fbm3( prec*vec3(pos.x, pos.y+kke, pos.z) )-kk);
    				s.Normal.z += bumpa*(fbm3( prec*vec3(pos.x, pos.y, pos.z+kke) )-kk);
    				s.Normal = normalize(s.Normal);
				}
    			#endif

				#if 0 //tiled Normal
				if(hitPositionWS.y < (HALLWAY_POS_Y - HALLWAY_EXT_Y + 0.1))
				{
					const float h = 0.01;
					vec2 funcinput = vec2(0.30) + hitPositionWS.xz * vec2(0.05, 0.2);
					vec2 n;
					float tileHeight = 0.25;
					n.x = tileHeightMap(funcinput + vec2(h, 0), tileHeight) - tileHeightMap(funcinput - vec2(h, 0), tileHeight);
					n.y = tileHeightMap(funcinput + vec2(0, h), tileHeight) - tileHeightMap(funcinput - vec2(0, h), tileHeight);
					n *= 2.0;
					s.Normal.y = n.x;
					s.Normal.z = n.y;
					s.Normal = normalize(s.Normal);
				}
				#endif

				s.PixelPos = hitPositionWS;
				s.vToCam = normalize(SceneBuffer.CameraPosition.xyz - s.PixelPos);

				float NDotV = max(dot(s.Normal, s.vToCam), 0.0);
				vec3 F0 = vec3(0.04);
				F0 = mix(F0, s.Albedo, s.Metalness);
				vec3 F = FresnelSchlickRoughness(NDotV, F0, s.Roughness);
				vec3 kS = F;
				vec3 kD = 1.0 - kS;

				float AO = 1.0;
				#if 1
				{
					//try using harsher normal
					AO *= CalcAOPrecise( s.PixelPos, s.Normal );
					float ran = hash( uint(gl_FragCoord.x) + 1920U* uint(gl_FragCoord.y) + (1920U*1080U) );  
					AO *= CalcAOSpread( s.PixelPos, s.Normal, ran );
					AO *= CalcAOSpreadHemisphere( s.PixelPos, s.Normal, 1., 0.5 );
					AO *= AO;
					//OutColor = vec3(AO * 0.5); return;
				}
				#endif

				#if 1
				if(hitPositionWS.z < (HALLWAY_EXT_Z + 0.1))
				{
					const vec3 hallCol = vec3(0.8, 0.075, 0.0);
					vec3 indirectDiffuse = hallCol * clamp(1.0 - length(s.PixelPos.y - 1.0 + 10.0) * 0.1, 0.0, 1.0) * 5.0;
					//indirectDiffuse = vec3(0.0);
					indirectDiffuse += hallCol * 0.5;
					float att = 1.0;
					if(hitPositionWS.z > HALLWAY_EXT_Z - 20.0)
					{
						//att *= clamp(MapToRange(hitPositionWS.z, HALLWAY_EXT_Z - 20.0, HALLWAY_EXT_Z, 1.0, -1.0), -1.0, 1.0);
					}
					light += att * vec3(indirectDiffuse * s.Albedo / PI * kD) * AO * (SceneBuffer.AmbientLight + 0.3);
				}
				#endif

				//Lights
				s.LightRadius = 100.0;
				s.LightColor = vec3(1.0, 0.1 * 0.5, 0.0);
				#if 1
					if(hitPositionWS.z < (HALLWAY_EXT_Z + 0.1))
					{
						float attAdd = 1.0;
						if(hitPositionWS.z > HALLWAY_EXT_Z - 10.0)
						{
							attAdd = clamp(MapToRange(hitPositionWS.z, HALLWAY_EXT_Z - 10.0, HALLWAY_EXT_Z - 0.5, 1.0, 0.5), 0.0, 1.0);
						}

						{
							float curLightZPos = s.PixelPos.z + 1.0;
							float curLightXPos = RedLineFunc(curLightZPos);
							s.LightPos = vec3(curLightXPos, -2.95, curLightZPos);

							float lightShadow = 1.0;
							lightShadow = SDFComputeShadow(s.PixelPos, s.LightPos);

							light += CalculateLightPBR(s) * lightShadow * attAdd;
						}

						{
							float curLightZPos = s.PixelPos.z - 1.0;
							float curLightXPos = RedLineFunc(curLightZPos);
							s.LightPos = vec3(curLightXPos, -2.95, curLightZPos);

							light += CalculateLightPBR(s) * attAdd;
						}
					}
					else
					{
					}
				#else
						vec3 lightDirection = normalize(vec3(0.25, 0.5, -0.75));
            			// diffuse
            			light = s.Albedo * abs(dot(lightDirection, s.Normal)) * 0.75;
            			// ambient
            			light += s.Albedo * 0.5 * ((s.Normal.y + 1.0) * 0.5);
				#endif

			}

			/* t = length(rayOrigin - hitPositionWS);
			float depth = clamp(t / 100.0, 0.0, 1.0);

			color.rgb = vec3(depth); */

			color.rgb = light;
		}

		


	#if 1 //Ray March
	{
		float marchMaxLength = 100.0;
		const int numMarchSamples = 64;
		float marchStepSize = marchMaxLength / float(numMarchSamples);

		vec3 scatter = vec3(0.0);

		float transmittance = 1.0;
		float sigmaE = 0.0;//extinction
    	float sigmaS = 0.0;//out-scatter

		float jitter = whangHashNoise(uint(gl_FragCoord.x) + 2000u, uint(gl_FragCoord.y) + 5000u, uint(SceneBuffer.Time * 30.0)*12345u );
		//jitter = fract(jitter + mod(SceneBuffer.Time * 100.0, 64.0) * kGoldenRatioConjugate);
		//jitter = fract(jitter + hashf(SceneBuffer.Time) * 0.1);
		for(int i = 0; i < numMarchSamples; i++)
		{
			float tt = jitter + marchStepSize * float(i + 1);

			if(tt > t)
			{
				break;
			}

			vec3 rayMarchCurPos = rayOrigin + tt * rayDir;

			//In scatter

			//Phase func
			#if 0
				const float g = 0.5;
				float k = 1.55*g - 0.55 * g*g*g;
				float cosTheta = dot(-l, rayDir);
				float p = (1.0 - k*k) / (4.0 * PI * pow(1.0 - cosTheta, 2.0)); 
			#else
				//isotropic
				const float p = 1.0/(4.0*PI);
			#endif

			float fogSpeedScale = 0.0;
			if(rayMarchCurPos.z > HALLWAY_EXT_Z)
			{
				fogSpeedScale = 1.0;
			}
			sigmaS = abs(hashPerlin3(rayMarchCurPos * 0.5 * 0.2 + vec3(-0.2, 0.1, 0.5) * SceneBuffer.Time * 2.35 * fogSpeedScale)) * 0.15;

			float heightAtt = clamp(MapToRange(rayMarchCurPos.y, SEA_POS_Y, SEA_POS_Y + 30.0, 1.0, 0.0), 0.0, 1.0);
			heightAtt *= heightAtt;
			sigmaS *= heightAtt;

			sigmaE = max(0.0000001, sigmaS);

			vec3 lightColor = vec3(1.0, 0.075 * 0.5, 0.0);

			vec3 curLight = vec3(0.0);
			if(rayMarchCurPos.z < (HALLWAY_EXT_Z))
			{
				float curLightZPos = rayMarchCurPos.z;
				float curLightXPos = RedLineFunc(curLightZPos);
				vec3 lightPos = vec3(curLightXPos, -2.95, curLightZPos);
				
				float d = length(lightPos - rayMarchCurPos);
				float attenuation = pow(clamp(1.f - pow(d / 10.0, 2.0), 0.f, 1.f), 2.0);

				float attAdd = 1.0;
				if(rayMarchCurPos.z > HALLWAY_EXT_Z - 10.0)
				{
					attAdd = clamp(MapToRange(rayMarchCurPos.z, HALLWAY_EXT_Z - 10.0, HALLWAY_EXT_Z, 1.0, 0.0), 0.0, 1.0);
					attenuation *= attAdd;
				}
				
				curLight = lightColor * attenuation * attenuation * attenuation * 10.75 * 0.7;

				curLight += SceneBuffer.AmbientLight * vec3(1.0, 0.5, 0.5) * attAdd * 2.64;
			}
			else
			{
				//pyr
				{
					float pyrExtent = 65.0;
					float pyrScale = 1.0 / pyrExtent;
					vec3 pyrPos = vec3(0.0, 41.0, 200.0);

					const float damb = pow(clamp(1.f - pow(1.0 / 10.0, 2.0), 0.f, 1.f), 2.0) * 0.4;
					curLight += vec3(1.0, 0.1, 0.0) * damb;

					float dToPyr = length(rayMarchCurPos.xz - pyrPos.xz);
					curLight += 3.0 * vec3(1.0, 0.05, 0.1) * pow(clamp(1.f - pow(dToPyr / 60.0, 2.0), 0.f, 1.f), 3.0) * 2.0 * SceneBuffer.PyramidBrightness * 0.1;
				}
			}

			scatter += sigmaS * curLight * p * transmittance * marchStepSize;

			//Transmittance
			transmittance *= exp(-sigmaE * marchStepSize);

		}
		
		color = color * transmittance + scatter;

	}
	#endif

		if(rayOrigin.y > SEA_POS_Y)
		{
			color.rgb *= vec3(1.0, 0.5, 0.75);
		}

		outColor = vec4(color.rgb, 1.0);

	}
	
	`;
}


export function GetShaderSourceTVScenePS(sceneSDFFunc : string) {
	return /* glsl */ `#version 300 es
	
	precision highp float;
	precision highp sampler2D;

	layout(location = 0) out vec4 outColor;

	in vec2 vsOutTexCoords;

	uniform sampler2D ProjectorTexture;
	uniform samplerCube CubemapTexture;

	uniform int ETVSwitchState;

	` +scDeclareSceneUniformBuffer()+/* glsl */`
	`+scGetCommonFuncsDeclarations()+ /* glsl */`
	`+scPBRDeclarations()+ /* glsl */`
	`+scGetSDFFuncsDeclarations(sceneSDFFunc)+ /* glsl */`

	
	vec2 crtCurve(vec2 uv, float r) 
	{
	    uv = (uv - 0.5) * 2.0;// uv is now -1 to 1
	    uv = r*uv/sqrt(r*r -dot(uv, uv));
	    uv = (uv / 2.0) + 0.5;// back to 0-1 coords
	    return uv;
	}

	float roundSquare(vec2 p, vec2 b, float r)
	{
	    return length(max(abs(p)-b,0.0))-r;
	}

	vec2 fault(vec2 uv, float s)
	{
    	//float v = (0.5 + 0.5 * cos(2.0 * pi * uv.y)) * (2.0 * uv.y - 1.0);
    	float v = pow(0.5 - 0.5 * cos(2.0 * 3.14 * uv.y), 100.0) * sin(2.0 * 3.14 * uv.y);
    	uv.x += v * s;
    	return uv;
	}

	float faultEx(vec2 uv, float s)
	{
    	//float v = (0.5 + 0.5 * cos(2.0 * pi * uv.y)) * (2.0 * uv.y - 1.0);
    	float v = pow(0.5 - 0.5 * cos(2.0 * 3.14 * uv.y), 100.0) * sin(2.0 * 3.14 * uv.y);
    	return v * s;
	}

	vec3 colorSplitSample(vec2 uv, vec2 s)
	{
	    vec3 color;
	    color.r = texture(ProjectorTexture, uv).r;
	    color.g = texture(ProjectorTexture, uv - s * 0.5).g;
	    color.b = texture(ProjectorTexture, uv + s).b;
	    return color;
	}

	vec3 getTVScreenColor(vec2 uv)
	{
		const float NO_OF_LINES = 1080.0 * 0.3;
		float scln = 0.5 - 0.5*cos(uv.y*3.14*NO_OF_LINES); // scanlines

		scln *= scln;

		if(ETVSwitchState > 1)
		{
			scln = 1.0;
		}

		float s = max(0.02, 1.1 - length(pow(2.0 * (uv - vec2(0.5)), vec2(4.0))));

		scln = mix(1.0, scln, sqrt(s));

		s *= scln;

		s = min(1.0, s);

		s = max(SceneBuffer.SDFHelperFloat2, s);

		s *= 1.0 + (1.0 - SceneBuffer.SDFHelperFloat2) * 0.5;

		float noiseBr = clamp(hashPerlin3(vec3(17.1, 0.13 , 0.02) * SceneBuffer.Time * 0.02), 0.0, 1.0);
		float noiseBr2 = clamp(hashPerlin3(vec3(7.1, 2.13 , 0.02) * SceneBuffer.Time * 0.01) * 3.5 * 4.5, 1.0, 3.5 * 4.5);
		//float noiseBr2 = 3.5 * 4.5;
		float speed  = noiseBr2 * 0.05;
		float sGl = 3.0 * sign(noiseBr) * pow(abs(noiseBr), 5.0);
		vec2 dUv = fault(uv + vec2(0.0, fract(SceneBuffer.Time * speed)), sGl) - vec2(0.0, fract(SceneBuffer.Time * speed));
		float dUvS = faultEx(uv + vec2(0.0, fract(SceneBuffer.Time * speed)), sGl); 
		noiseBr = clamp(hashPerlin3(vec3(0.1, 1.0 , 3.02) * SceneBuffer.Time * 2.4), 0.0, 1.0);
		vec3 col = colorSplitSample(dUv , vec2(dUvS, noiseBr * 0.01));
		float noise = hashf(uv.y * SceneBuffer.Time + uv.x) * 0.75;
		col = mix(vec3(noise), col, 0.95 - clamp(sGl, 0.0, 0.25));
		if(ETVSwitchState > 1)
		{
			col = textureLod(ProjectorTexture, uv, 0.f).rgb;
		}	
		

		//col *= col;
		if(ETVSwitchState > 1)
		{
			s *= 0.4;
		}

		return s * col;
	}

	vec4 GetTVColor(vec2 uv)
	{
		vec3 c = vec3(0.0);
		float r = 2.5;

		vec2 uvS = crtCurve(uv, r);
		uvS = mix(uvS, uv, SceneBuffer.SDFHelperFloat2);

		// Screen Content
		vec2 uvC = (uvS - 0.5)* 2.0; // screen content coordinate system
		uvC *= 1.3;
		uvC = (uvC / 2.0) + 0.5;

		vec2 uvU = uvC;

		if(uvU.x < 0.0 || uvU.x > 1.0 ||  uvU.y < 0.0 || uvU.y > 1.0)
		{

		}
		else
		{
			c += getTVScreenColor(uvU);
		}

		vec2 uvE = crtCurve(uv, r+0.25);
		uvE = mix(uvE, uv, SceneBuffer.SDFHelperFloat2);

		const float SIZE = 0.37;
		const float SMOOTH = 0.0025;

		vec3 borders = vec3(0.0);

		const float innerSize = 0.005;
		const float outerSize = 0.075;

		const float rad = 0.015;
		
		float alpha = smoothstep(SMOOTH, -SMOOTH, roundSquare(uvE-vec2(0.5, 0.5), vec2(SIZE) + outerSize, rad));
		float returnAlpha = alpha;

		float s = max(0.05, 1.1 - length(pow(2.0 * (uvS - vec2(0.5)), vec2(1.0))));

		float addBr = 0.5;

		float noiseBr = clamp(hashPerlin3(vec3(0.1 + uv.x * 0.1, 1.0 + uv.y * 0.02 , 3.02) * SceneBuffer.Time * 10.4), 0.75, 1.0);

		if(ETVSwitchState > 1)
		{
			s *= 0.1 * 0.5;
			alpha *= 0.7;
		}

		borders += max(0.75, 1.0 - uv.y) * noiseBr * s * 3.5 * 
        smoothstep(-SMOOTH, SMOOTH, roundSquare(uvS-vec2(0.5, 0.5), vec2(SIZE), rad)) * 
        smoothstep(SMOOTH, -SMOOTH, roundSquare(uvE-vec2(0.5, 0.5), vec2(SIZE) + innerSize, rad));
    
    	// Outer Border
    	borders += 0.1 * 
       	smoothstep(-SMOOTH, SMOOTH, roundSquare(uvE-vec2(0.5, 0.5), vec2(SIZE) + innerSize, rad)) * 
        alpha;

		borders *= addBr;
		borders = max(vec3(0.0), borders);

		const float switchExt = 0.03;
		const vec2 switchCenter = vec2(0.905, 0.07);
		vec2 uvSw = clamp(uvE - switchCenter, vec2(0.0), vec2(1.0));

		if( uvSw.x > 0.0 && uvSw.x < switchExt && uvSw.y > 0.0 && uvSw.y < switchExt)
		{
			vec2 uvL = uvSw / switchExt;
			float s = max(0.02, 1.1 - length(pow(2.0 * (uvL - vec2(0.5)), vec2(8.0))));

			vec3 buttonCol = vec3(1.0);
			if(ETVSwitchState == 0)
			{
				buttonCol *= 1.5;
			}
			if(ETVSwitchState == 1)
			{
				buttonCol *= 2.0;
			}
			if(ETVSwitchState == 2)
			{
				buttonCol = vec3(1.0, 0.2, 0.1);
			}

			borders = buttonCol * 2.0 * s * s;
		}

		//borders *= 0.0;

		return vec4(borders * 0.1 + c, returnAlpha);
	}

	
	void main()
	{
		//vec3 rayOrigin = SceneBuffer.CameraPosition;
		float rzp = -16.0;
		rzp = mix(-26.0, -8.75, SceneBuffer.SDFHelperFloat1);
		vec3 rayOrigin = vec3(0.0, 2.75, rzp);
		rayOrigin.z -= 0.1;
		vec3 rayDir;

		vec3 color = vec3(0.8, 0.1, 0.0) * 0.0;

		//NDC to world
		{
			vec2 posNDC = MapToRange(vsOutTexCoords, 0.0, 1.0, -1.0, 1.0);
			vec3 posWS = vec3(posNDC.x * SceneBuffer.ScreenRatio, posNDC.y, 0.0);
			posWS.xy /= SceneBuffer.CameraZoom;
			const float farPlane = 20.0;
			posWS.xy *= farPlane;
			posWS.xy += rayOrigin.xy;
			posWS.z = rayOrigin.z + farPlane;
			rayDir = normalize(posWS - rayOrigin);
		}

		

		vec3 light = vec3(0.0, 0.0, 0.01);
		
		//env probe
		vec3 envColor = vec3(0.0);
		{
			vec3 samplePos = rayDir;
			samplePos.y *= -1.0;
			samplePos.xy *= 1.2;
			/* //samplePos.xy *= 0.45;
			samplePos.xy *= 3.0;
			samplePos.y *= -1.0; */
			{
				float angle = 4.65;
				mat3 rotationMatrix = mat3(
					cos(angle), 0.0, sin(angle),
					0.0,  1.0, 0.0,
				   -1.0*sin(angle), 0.0, cos(angle)
				);
				samplePos = rotationMatrix * samplePos;
			}
			//get relfected color
			int numMips = 10;
			float envMip = 0.4 * float(numMips - 1);
			vec3 prefilteredColor = textureLod(CubemapTexture, samplePos, envMip).rgb;
			/* vec2 envBRDF = texture(BRDF_LUT, vec2(NDotV, (s.Roughness))).rg;
			vec3 fScale = vec3(F * envBRDF.x + envBRDF.y) * (1.0 - s.Metalness);
			vec3 indirectSpecular = prefilteredColor * fScale; */
			envColor += prefilteredColor * 0.025;
		}
		
		
		vec3 hitPositionWS = rayOrigin;
		float t = 0.01;

		//check TV intersection
		const vec3 tvPos = vec3(0.0, 2.75, 20.0);
		const float tvSize = 15.0;
		float ratio = mix(1.35, SceneBuffer.ScreenRatio, SceneBuffer.SDFHelperFloat2);
		vec3 tvExt = vec3(tvSize * ratio, tvSize, 0.01);
		
		{
			vec3 tvDirVec = rayDir;
			vec3 distOffset = vec3(0.0); 
			distOffset.xy = fbm22(vec2(0.1, 1.0) * SceneBuffer.Time * 0.02);
			tvDirVec = normalize(tvDirVec + distOffset * 0.1 * 0.2 * (1.0 - SceneBuffer.SDFHelperFloat2));
			vec4 rayTVInterTest = RayAABBIntersection(rayOrigin, tvDirVec, tvPos, tvExt);
			if(rayTVInterTest.x > 0.0)
			{
				//tvT = rayTVInterTest.y;
				vec3 tvHitPos = rayOrigin + tvDirVec * rayTVInterTest.y;
				vec2 tvUV;
				tvUV.x = MapToRange(tvHitPos.x, tvPos.x - tvExt.x, tvPos.x + tvExt.x, 0.0, 1.0);
				tvUV.y = MapToRange(tvHitPos.y, tvPos.y - tvExt.y, tvPos.y + tvExt.y, 0.0, 1.0);
				vec4 tvColor = GetTVColor(tvUV);
				light += tvColor.rgb;
				//envColor *= max(0.25, (1.0 - tvColor.a));
			}
			light += envColor;
		}

		outColor = vec4(light, 1.0);

				

	}
	`;
}

